import { useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Users, MoreHorizontal, X } from "lucide-react";
import { useHomeOSState } from "../state/HomeOSStateProvider";
import {
  getOrderedWeekDays, getMealSlots, longDay,
  getTodayDayShort, getMealSlotByDayAndSlot, isSlotEmpty,
} from "../utils/mealPlanUtils";
import { MealSlotEditModal } from "./MealSlotEditModal";
import { MealPlanSettingsModal } from "./MealPlanSettingsModal";
import { MealMoveConfirmModal } from "./MealMoveConfirmModal";
import { MealMoveTargetPickerModal } from "./MealMoveTargetPickerModal";
import { getHouseholdFoodContext, evaluateMealForHousehold, conflictNames } from "../utils/foodIntelligence";

/* ════════════════════════════════════════════════════════════════════════
   THIS WEEK — the Kitchen planning surface.

   Calm paper planning table, not a spreadsheet. Hairlines only. Every
   colour lives on tiny meal-type accent dots. Cell content is minimal:
   meal name (Inter medium), cook, cook-time, status. No macros. No kcal.
   Hover reveals the four cell actions.

   Days across the top, meal slots down the side. Same layout on every
   viewport; the row scrolls horizontally on narrow screens.

   All existing handlers (drag-and-drop, action menu, modals, testids)
   are preserved — presentation only has been redrawn to the Residence
   Journal system.
   ════════════════════════════════════════════════════════════════════════ */

const DRAG_MIME = "application/x-ara-meal-slot";
export const RECIPE_DRAG_MIME = "application/x-ara-recipe-card";

/* Meal-type accents — tiny dots only, never fills. Sourced from the
   locked palette + reserved accent hues (Blush, Warm Linen, Terracotta,
   Warm Gold). */
const SLOT_ACCENT = {
  breakfast:    "var(--ara-mod-meals)",   // Soft Sage
  morningTea:   "#D9AFA6",                // Blush pink
  lunch:        "#C7B79A",                // Warm linen
  afternoonTea: "var(--ara-mod-health)",  // Soft eucalyptus
  dinner:       "#B57662",                // Terracotta
  dessert:      "var(--ara-mod-goals)",   // Warm gold / brass
};

function slotAccent(slotId) {
  return SLOT_ACCENT[slotId] || "var(--ara-ink-soft)";
}

function shortDate(dayShort, orderedDays, weekStart = new Date()) {
  // Best-effort: compute a date for each ordered day starting from weekStart.
  const idx = orderedDays.indexOf(dayShort);
  if (idx < 0) return "";
  const d = new Date(weekStart);
  d.setDate(d.getDate() + idx);
  return d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });
}

export function WeeklyMealTable({ onPrint }) {
  const state = useHomeOSState();
  const {
    weeklyMealPlan, mealPlanSettings, recipes, members,
    moveMealSlot, swapMealSlots, duplicateMealSlot, clearMealSlot,
    setMealSlotFromRecipe,
    kitchenCookingFor,
  } = state;

  const [editTarget, setEditTarget] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [dragSource, setDragSource] = useState(null);
  const [dropHover, setDropHover] = useState(null);
  const [confirmCtx, setConfirmCtx] = useState(null);
  const [recipeReplaceCtx, setRecipeReplaceCtx] = useState(null);
  const [actionMenu, setActionMenu] = useState(null);
  const [pickerCtx, setPickerCtx] = useState(null);

  const orderedDays = useMemo(
    () => getOrderedWeekDays(mealPlanSettings?.weekStartDay || "Fri"),
    [mealPlanSettings?.weekStartDay]
  );
  const slotsCfg = useMemo(() => getMealSlots(mealPlanSettings), [mealPlanSettings]);
  const todayShort = getTodayDayShort();

  const dayByCode = useMemo(() => {
    const map = {};
    (weeklyMealPlan || []).forEach(d => { map[d.day] = d; });
    return map;
  }, [weeklyMealPlan]);

  const recipeById = useMemo(() => {
    const map = {};
    (recipes || []).forEach(r => { map[r.id] = r; });
    return map;
  }, [recipes]);

  const memberById = useMemo(() => {
    const map = { all: { id: "all", name: "Everyone", isAll: true } };
    (members || []).forEach(m => { map[m.id] = m; });
    return map;
  }, [members]);

  const slotCfgById = useMemo(() => {
    const m = {};
    slotsCfg.forEach(c => { m[c.id] = c; });
    return m;
  }, [slotsCfg]);

  /* Derived family food context — read once per render, not per cell. */
  const foodCtx = useMemo(
    () => getHouseholdFoodContext(members, kitchenCookingFor),
    [members, kitchenCookingFor]
  );

  /* ── Drag-and-drop handlers ─────────────────────────────────────── */
  const onCellDragStart = (e, day, slotId) => {
    const slot = dayByCode[day]?.slots?.[slotId];
    if (!slot || isSlotEmpty(slot)) { e.preventDefault(); return; }
    setDragSource({ day, slotId });
    setActionMenu(null);
    try {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData(DRAG_MIME, JSON.stringify({ day, slotId }));
      e.dataTransfer.setData("text/plain", `${slot.title || "meal"}`);
    } catch { /* some browsers throw on custom mime */ }
  };
  const onCellDragOver = (e, day, slotId) => {
    const types = Array.from(e.dataTransfer?.types || []);
    const isRecipeDrop = types.includes(RECIPE_DRAG_MIME);
    if (!dragSource && !isRecipeDrop) return;
    if (dragSource && dragSource.day === day && dragSource.slotId === slotId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropHover({ day, slotId, kind: isRecipeDrop ? "recipe" : "slot" });
  };
  const onCellDragLeave = (day, slotId) => {
    setDropHover(prev => (prev && prev.day === day && prev.slotId === slotId ? null : prev));
  };
  const onCellDrop = (e, day, slotId) => {
    e.preventDefault();
    setDropHover(null);
    const recipeRaw = e.dataTransfer?.getData(RECIPE_DRAG_MIME);
    if (recipeRaw) {
      let recipeId = null;
      try { ({ recipeId } = JSON.parse(recipeRaw)); } catch { /* ignore */ }
      const recipe = recipeId ? (recipes || []).find(r => r.id === recipeId) : null;
      if (!recipe) { toast("Couldn't find that recipe."); return; }
      const tgtSlot = getMealSlotByDayAndSlot(weeklyMealPlan || [], day, slotId);
      if (isSlotEmpty(tgtSlot)) {
        setMealSlotFromRecipe(day, slotId, recipe);
        toast.success(`${recipe.title} added to ${longDay(day)} ${slotCfgById[slotId]?.label?.toLowerCase() || "slot"}.`);
        return;
      }
      setRecipeReplaceCtx({
        recipe,
        target: { day, slotId, slotLabel: slotCfgById[slotId]?.label || slotId, slot: tgtSlot },
      });
      return;
    }
    if (!dragSource) return;
    const src = dragSource;
    setDragSource(null);
    if (src.day === day && src.slotId === slotId) return;
    const tgtSlot = getMealSlotByDayAndSlot(weeklyMealPlan || [], day, slotId);
    if (isSlotEmpty(tgtSlot)) {
      moveMealSlot(src.day, src.slotId, day, slotId);
      toast.success(`Meal moved to ${longDay(day)} ${slotCfgById[slotId]?.label?.toLowerCase() || "slot"}.`);
      return;
    }
    const srcSlot = getMealSlotByDayAndSlot(weeklyMealPlan || [], src.day, src.slotId);
    setConfirmCtx({
      source: { day: src.day, slotId: src.slotId, slotLabel: slotCfgById[src.slotId]?.label || src.slotId, slot: srcSlot },
      target: { day,           slotId,           slotLabel: slotCfgById[slotId]?.label || slotId,           slot: tgtSlot },
    });
  };
  const onCellDragEnd = () => { setDragSource(null); setDropHover(null); };

  const handleSwap = () => {
    if (!confirmCtx) return;
    const { source, target } = confirmCtx;
    swapMealSlots(source.day, source.slotId, target.day, target.slotId);
    toast.success("Meals swapped.");
    setConfirmCtx(null);
  };
  const handleReplace = () => {
    if (!confirmCtx) return;
    const { source, target } = confirmCtx;
    moveMealSlot(source.day, source.slotId, target.day, target.slotId);
    toast.success(`Meal moved to ${longDay(target.day)} ${target.slotLabel.toLowerCase()}.`);
    setConfirmCtx(null);
  };
  const handleRecipeReplace = () => {
    if (!recipeReplaceCtx) return;
    const { recipe, target } = recipeReplaceCtx;
    setMealSlotFromRecipe(target.day, target.slotId, recipe, { keepCook: true, existingCook: target.slot?.assignedCook || null });
    toast.success(`${longDay(target.day)} ${target.slotLabel.toLowerCase()} replaced with ${recipe.title}.`);
    setRecipeReplaceCtx(null);
  };

  /* ── Action menu (touch/mouse fallback) ──────────────────────────── */
  const openActionMenu = (day, slotId, anchor) => { setActionMenu({ day, slotId, anchorRect: anchor }); };
  const closeActionMenu = () => setActionMenu(null);
  const onMenuMove      = (day, slotId) => { closeActionMenu(); setPickerCtx({ mode: "move",      source: { day, slotId, slotLabel: slotCfgById[slotId]?.label || slotId } }); };
  const onMenuDuplicate = (day, slotId) => { closeActionMenu(); setPickerCtx({ mode: "duplicate", source: { day, slotId, slotLabel: slotCfgById[slotId]?.label || slotId } }); };
  const onMenuSwap      = (day, slotId) => { closeActionMenu(); setPickerCtx({ mode: "swap",      source: { day, slotId, slotLabel: slotCfgById[slotId]?.label || slotId } }); };
  const onMenuClear     = (day, slotId) => {
    closeActionMenu();
    if (typeof window !== "undefined" && !window.confirm("Clear this meal from the plan?")) return;
    clearMealSlot(day, slotId);
    toast.success("Meal cleared.");
  };
  const onMenuEdit      = (day, slotId) => { closeActionMenu(); setEditTarget({ day, slotId }); };

  const handlePickerPick = (target) => {
    if (!pickerCtx) return;
    const src = pickerCtx.source;
    const mode = pickerCtx.mode;
    const tgtSlot = getMealSlotByDayAndSlot(weeklyMealPlan || [], target.day, target.slotId);
    const tgtLabel = slotCfgById[target.slotId]?.label || target.slotId;
    if (mode === "swap") {
      swapMealSlots(src.day, src.slotId, target.day, target.slotId);
      toast.success("Meals swapped.");
      setPickerCtx(null); return;
    }
    if (mode === "duplicate") {
      if (!isSlotEmpty(tgtSlot)) {
        if (typeof window !== "undefined" && !window.confirm(`Replace existing meal in ${longDay(target.day)} ${tgtLabel.toLowerCase()}?`)) {
          setPickerCtx(null); return;
        }
      }
      duplicateMealSlot(src.day, src.slotId, target.day, target.slotId);
      toast.success(`Meal duplicated to ${longDay(target.day)} ${tgtLabel.toLowerCase()}.`);
      setPickerCtx(null); return;
    }
    if (!isSlotEmpty(tgtSlot)) {
      const srcSlot = getMealSlotByDayAndSlot(weeklyMealPlan || [], src.day, src.slotId);
      setPickerCtx(null);
      setConfirmCtx({
        source: { day: src.day,    slotId: src.slotId,    slotLabel: src.slotLabel, slot: srcSlot },
        target: { day: target.day, slotId: target.slotId, slotLabel: tgtLabel,      slot: tgtSlot },
      });
      return;
    }
    moveMealSlot(src.day, src.slotId, target.day, target.slotId);
    toast.success(`Meal moved to ${longDay(target.day)} ${tgtLabel.toLowerCase()}.`);
    setPickerCtx(null);
  };

  const openEdit = (day, slotId) => setEditTarget({ day, slotId });
  const closeEdit = () => setEditTarget(null);

  // Compute week-start monday
  const weekStart = useMemo(() => {
    const today = new Date();
    const currIdx = today.getDay(); // 0-6 with Sun=0
    const startIdx = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].indexOf(orderedDays[0]);
    const diff = (currIdx - startIdx + 7) % 7;
    const d = new Date(today);
    d.setDate(d.getDate() - diff);
    return d;
  }, [orderedDays]);

  /* ── Render ──────────────────────────────────────────────────────── */
  return (
    <section data-testid="weekly-meal-table" className="min-w-0 max-w-full">
      {/* Header row: soft actions on the right. No coloured pills. */}
      <div className="flex items-baseline justify-between mb-6 flex-wrap gap-3">
        <p className="caption-ara" data-testid="weekly-meal-week-range">
          {longDay(orderedDays[0])} → {longDay(orderedDays[orderedDays.length - 1])}
        </p>
        <div className="flex items-baseline gap-x-6">
          {onPrint && (
            <button
              type="button"
              onClick={onPrint}
              className="link-ara"
              data-testid="weekly-meal-print"
            >
              Print →
            </button>
          )}
          <button
            type="button"
            onClick={() => setSettingsOpen(true)}
            className="link-ara"
            data-testid="weekly-meal-settings-btn"
          >
            Meal plan settings
          </button>
        </div>
      </div>

      {/* The planning surface — hairlines only, no cell borders */}
      <div className="w-full max-w-full min-w-0" data-testid="weekly-meal-table-grid">
        <p className="caption-ara md:hidden mb-3" style={{ color: 'var(--ara-ink-soft)' }}>
          Swipe sideways to see the whole week
        </p>
        <div className="overflow-x-auto">
          <div className="min-w-fit" style={{ minWidth: `${132 + orderedDays.length * 132}px` }}>
            {/* Header row — days across the top */}
            <div
              className="grid sticky top-0 z-10"
              style={{
                gridTemplateColumns: `132px repeat(${orderedDays.length}, minmax(132px, 1fr))`,
                background: 'var(--ara-surface)',
                borderBottom: '1px solid var(--ara-line)',
              }}
            >
              <div className="px-2 py-3 caption-ara">Meal</div>
              {orderedDays.map(d => {
                const isToday = d === todayShort;
                return (
                  <div
                    key={d}
                    className="px-3 py-3"
                    data-testid={`weekly-meal-day-header-${d}`}
                  >
                    <p
                      className="text-[13px] leading-tight"
                      style={{
                        fontFamily: 'Inter, system-ui, sans-serif',
                        color: isToday ? 'var(--ara-ink)' : 'var(--ara-ink-muted)',
                        fontWeight: isToday ? 500 : 400,
                      }}
                    >
                      {longDay(d)}
                    </p>
                    <p className="mt-0.5 caption-ara" style={{ letterSpacing: '0.12em' }}>
                      {shortDate(d, orderedDays, weekStart)}
                      {isToday ? ' · today' : ''}
                    </p>
                  </div>
                );
              })}
            </div>

            {/* Slot rows */}
            {slotsCfg.map(cfg => (
              <div
                key={cfg.id}
                className="grid"
                style={{
                  gridTemplateColumns: `132px repeat(${orderedDays.length}, minmax(132px, 1fr))`,
                  borderBottom: '1px solid var(--ara-line-soft)',
                }}
              >
                <div className="px-2 py-4 flex items-center gap-2" data-testid={`weekly-meal-slot-label-${cfg.id}`}>
                  <span className="dot-ara" style={{ background: slotAccent(cfg.id) }} aria-hidden />
                  <p
                    className="text-[13px] leading-tight"
                    style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                  >
                    {cfg.label}
                  </p>
                </div>
                {orderedDays.map(d => {
                  const dayObj = dayByCode[d];
                  const slot = dayObj?.slots?.[cfg.id];
                  const isDragSource = dragSource && dragSource.day === d && dragSource.slotId === cfg.id;
                  const isDropHover  = dropHover  && dropHover.day  === d && dropHover.slotId  === cfg.id;
                  const tgtFilled    = !isSlotEmpty(slot);
                  const isRecipeDrop = isDropHover && dropHover?.kind === "recipe";
                  return (
                    <div
                      key={`${d}-${cfg.id}`}
                      className={`relative transition-ara ${isDragSource ? "opacity-40" : ""}`}
                      style={{
                        outline: isDropHover ? `1px solid var(--ara-ink)` : 'none',
                        outlineOffset: '-1px',
                        background: isDropHover ? 'rgba(31,31,31,0.03)' : 'transparent',
                      }}
                      onDragOver={(e) => onCellDragOver(e, d, cfg.id)}
                      onDragLeave={() => onCellDragLeave(d, cfg.id)}
                      onDrop={(e) => onCellDrop(e, d, cfg.id)}
                      data-testid={`weekly-meal-cell-wrap-${d}-${cfg.id}`}
                    >
                      <MealCell
                        slotCfg={cfg}
                        slot={slot}
                        recipe={slot?.recipeId ? recipeById[slot.recipeId] : null}
                        cook={slot?.assignedCook ? memberById[slot.assignedCook] : null}
                        showAssignedCook={mealPlanSettings?.showAssignedCook !== false}
                        onClick={() => openEdit(d, cfg.id)}
                        onDragStart={(e) => onCellDragStart(e, d, cfg.id)}
                        onDragEnd={onCellDragEnd}
                        onActionMenu={(rect) => openActionMenu(d, cfg.id, rect)}
                        dayCode={d}
                        foodCtx={foodCtx}
                      />
                      {isDropHover && (
                        <p
                          className="pointer-events-none absolute inset-x-2 bottom-1 caption-ara text-center"
                          style={{ color: 'var(--ara-ink)' }}
                        >
                          {isRecipeDrop
                            ? (tgtFilled ? "Replace" : "Drop here")
                            : (tgtFilled ? "Swap" : "Move here")}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
        <p className="mt-6 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
          Drag a meal to move it. Tap a meal to view, edit or swap.
        </p>
      </div>

      <MealSlotEditModal open={!!editTarget} day={editTarget?.day} slotId={editTarget?.slotId} onClose={closeEdit} />
      <MealPlanSettingsModal open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <MealMoveConfirmModal
        open={!!confirmCtx}
        source={confirmCtx?.source}
        target={confirmCtx?.target}
        onSwap={handleSwap}
        onReplace={handleReplace}
        onClose={() => setConfirmCtx(null)}
      />
      <MealMoveConfirmModal
        open={!!recipeReplaceCtx}
        source={recipeReplaceCtx ? {
          day: "Family Recipe Book",
          slotId: "recipe",
          slotLabel: recipeReplaceCtx.recipe?.category || "Recipe",
          slot: { title: recipeReplaceCtx.recipe?.title || "", recipeTitle: recipeReplaceCtx.recipe?.title || "" },
        } : null}
        target={recipeReplaceCtx?.target}
        onReplace={handleRecipeReplace}
        onClose={() => setRecipeReplaceCtx(null)}
      />
      <MealMoveTargetPickerModal
        open={!!pickerCtx}
        mode={pickerCtx?.mode}
        source={pickerCtx?.source}
        weeklyMealPlan={weeklyMealPlan || []}
        orderedDays={orderedDays}
        slotsCfg={slotsCfg}
        onPick={handlePickerPick}
        onClose={() => setPickerCtx(null)}
      />
      {actionMenu && (
        <MealCellActionMenu
          anchorRect={actionMenu.anchorRect}
          onClose={closeActionMenu}
          onMove={() => onMenuMove(actionMenu.day, actionMenu.slotId)}
          onDuplicate={() => onMenuDuplicate(actionMenu.day, actionMenu.slotId)}
          onSwap={() => onMenuSwap(actionMenu.day, actionMenu.slotId)}
          onEdit={() => onMenuEdit(actionMenu.day, actionMenu.slotId)}
          onClear={() => onMenuClear(actionMenu.day, actionMenu.slotId)}
        />
      )}
    </section>
  );
}

/* ── Single meal cell ─────────────────────────────────────────────── */
function MealCell({
  slotCfg, slot, recipe, cook,
  showAssignedCook,
  onClick, onDragStart, onDragEnd, onActionMenu, dayCode,
  foodCtx,
}) {
  const empty = !slot || (!slot.title && !slot.recipeId);
  const menuBtnRef = useRef(null);
  /* Quiet family suitability — conflict-level only, one small caption. */
  const conflictWho = useMemo(() => {
    if (empty || !foodCtx?.hasAnyFoodInfo) return null;
    const evaluation = evaluateMealForHousehold({ recipe, title: slot?.title }, foodCtx);
    if (evaluation.status !== "conflict") return null;
    return { names: conflictNames(evaluation), line: evaluation.line };
  }, [empty, recipe, slot?.title, foodCtx]);
  const openMenu = (e) => {
    e.stopPropagation();
    const rect = menuBtnRef.current?.getBoundingClientRect();
    onActionMenu?.(rect ? { top: rect.bottom, left: rect.left, right: rect.right } : null);
  };

  if (empty) {
    return (
      <button
        onClick={onClick}
        className="w-full min-h-[104px] px-3 py-3 text-left transition-ara group"
        style={{ color: 'var(--ara-ink-soft)' }}
        data-testid={`weekly-meal-cell-empty-${dayCode}-${slotCfg.id}`}
      >
        <span
          className="text-[13px] opacity-0 group-hover:opacity-100 transition-ara"
          style={{ fontFamily: 'Inter, system-ui, sans-serif' }}
        >
          + Add meal
        </span>
      </button>
    );
  }

  const cookTime = recipe ? (recipe.prepTime || 0) + (recipe.cookTime || 0) : null;
  const status = slot.status && slot.status !== "Planned" ? slot.status : null;

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className="group relative w-full text-left px-3 py-3 cursor-grab active:cursor-grabbing transition-ara min-h-[104px]"
      data-testid={`weekly-meal-cell-${dayCode}-${slotCfg.id}`}
    >
      <button
        onClick={onClick}
        className="w-full text-left focus:outline-none"
        aria-label="Edit meal"
        data-testid={`weekly-meal-cell-open-${dayCode}-${slotCfg.id}`}
      >
        <p
          className="text-[14px] leading-snug line-clamp-2 pr-5"
          style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
        >
          {slot.title || recipe?.title || "Meal"}
        </p>
        <div className="mt-2 flex items-center gap-x-3 flex-wrap">
          {showAssignedCook && cook && (
            <span className="inline-flex items-center gap-1 caption-ara" style={{ letterSpacing: '0.1em' }}>
              <Users className="w-3 h-3" strokeWidth={1.5} aria-hidden />
              {cook.isAll ? 'Everyone' : cook.name.split(' ')[0]}
            </span>
          )}
          {cookTime > 0 && (
            <span className="caption-ara tabular-nums" style={{ letterSpacing: '0.1em' }}>
              {cookTime} min
            </span>
          )}
        </div>
        {status && (
          <p className="mt-1 caption-ara" style={{ color: 'var(--ara-ink-soft)' }}>
            {status}
          </p>
        )}
        {conflictWho && (
          <p
            className="mt-1 caption-ara"
            style={{ color: 'var(--ara-ink-muted)' }}
            title={conflictWho.line}
            data-testid={`weekly-meal-check-${dayCode}-${slotCfg.id}`}
          >
            Check for {conflictWho.names.join(" · ")}
          </p>
        )}
      </button>

      <button
        ref={menuBtnRef}
        type="button"
        onClick={openMenu}
        onPointerDown={(e) => e.stopPropagation()}
        draggable={false}
        className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center transition-ara opacity-0 group-hover:opacity-100"
        style={{ color: 'var(--ara-ink-soft)' }}
        aria-label="Meal actions"
        data-testid={`weekly-meal-cell-menu-btn-${dayCode}-${slotCfg.id}`}
      >
        <MoreHorizontal className="w-3.5 h-3.5" strokeWidth={1.5} />
      </button>
    </div>
  );
}

/* ── Action menu popover ──────────────────────────────────────────── */
function MealCellActionMenu({ anchorRect, onClose, onMove, onDuplicate, onSwap, onEdit, onClear }) {
  const pos = anchorRect
    ? { top: Math.min(anchorRect.top + 4, window.innerHeight - 240), left: Math.min(anchorRect.left - 140, window.innerWidth - 220) }
    : null;

  const items = [
    { id: "edit",      label: "View recipe",   onClick: onEdit },
    { id: "swap",      label: "Swap",          onClick: onSwap },
    { id: "move",      label: "Move",          onClick: onMove },
    { id: "duplicate", label: "Duplicate",     onClick: onDuplicate },
    { id: "clear",     label: "Clear",         onClick: onClear },
  ];

  return (
    <div
      className="fixed inset-0 z-[110]"
      onClick={onClose}
      data-testid="meal-cell-action-menu-overlay"
    >
      <div
        className="absolute w-[180px] bg-white overflow-hidden"
        style={{
          top: pos ? pos.top : '50%',
          left: pos ? Math.max(8, pos.left) : '50%',
          transform: pos ? 'none' : 'translate(-50%, -50%)',
          background: 'var(--ara-surface)',
          border: '1px solid var(--ara-line)',
        }}
        onClick={(e) => e.stopPropagation()}
        role="menu"
        data-testid="meal-cell-action-menu"
      >
        <div className="flex items-center justify-between px-4 py-2.5" style={{ borderBottom: '1px solid var(--ara-line-soft)' }}>
          <p className="caption-ara">Meal</p>
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center"
            style={{ color: 'var(--ara-ink-soft)' }}
            aria-label="Close"
            data-testid="meal-cell-action-menu-close"
          >
            <X className="w-3.5 h-3.5" strokeWidth={1.5} />
          </button>
        </div>
        <div className="py-1">
          {items.map(({ id, label, onClick }) => (
            <button
              key={id}
              onClick={onClick}
              className="w-full px-4 py-2.5 text-left text-[13px] transition-ara hover:opacity-70"
              style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 400 }}
              data-testid={`meal-cell-action-${id}`}
              role="menuitem"
            >
              {label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default WeeklyMealTable;
